import React from "react";
import PropTypes from "prop-types";
import selectn from "selectn";

import AnalyticsWrapper from "stream/components/AnalyticsWrapper";
import PaginatedStream from "stream/components/PaginatedStream";
import StreamSpinner from "stream/components/StreamSpinner";
import StreamTitle from "stream/components/StreamTitle";
import StreamErrorWrapper from "stream/components/StreamErrorWrapper";
import streamFetchMore from "stream/utils/streamFetchMore";
import { setLastSeenCookie } from "stream/utils/lastSeenCookie";

import {
  APOLLO_NETWORK_STATUS,
  STREAM_TYPE,
  TRACK_LAST_SEEN,
} from "config/constants.js";

export default function StreamWithData(props) {
  const { networkStatus, error, data, fetchMore, type } = props; // the render props

  // Loading — don't replace stream with loading spinner on fetchMore (networkStatus = 3)
  if (
    [
      APOLLO_NETWORK_STATUS.LOADING,
      APOLLO_NETWORK_STATUS.SET_VARIABLES,
      APOLLO_NETWORK_STATUS.REFETCH,
    ].includes(networkStatus)
  ) {
    return <StreamSpinner />;
  }

  // General empty state catcher
  // TODO: Move the noResultsComponent into the render prop.
  if (
    !(networkStatus === APOLLO_NETWORK_STATUS.ERROR || error) &&
    !data.stream.streamInclusionConnection.edges.length
  ) {
    return props.noResultsComponent;
  }

  const edges = selectn("stream.streamInclusionConnection.edges", data);
  const pageInfo = selectn("stream.streamInclusionConnection.pageInfo", data);
  const newLastSeenId = selectn(
    "stream.streamInclusionConnection.lastSeen",
    data
  );
  const unreadCount = selectn(
    "stream.streamInclusionConnection.unreadCount",
    data
  );
  const override = selectn(
    "stream.streamInclusionConnection.streamOverride.slug",
    data
  );

  const stream = {
    type: type,
    lookup: props.lookup,
    displayName: selectn("stream.displayName", data),
    override: override,
    extraContent: selectn(
      "stream.streamInclusionConnection.extraContentSection",
      data
    ),
  };

  // If the query has a lastSeenId, update the cookie
  if (TRACK_LAST_SEEN.includes(props.type) && newLastSeenId) {
    setLastSeenCookie(stream, newLastSeenId);
  }

  const edgesToRender = props.isClient
    ? edges
    : edges.slice(0, props.serverRenderedNodeCount);

  return (
    <StreamErrorWrapper networkStatus={networkStatus} error={error}>
      <StreamTitle
        text={stream.displayName}
        a11yOnly={props.onlyShowTitleForA11y}
        unreadCount={unreadCount}
      />
      <AnalyticsWrapper
        stream={stream}
        disableAnalytics={props.disableAnalytics}
        disableUrlUpdating={props.disableUrlUpdating}
        trackFirstItem={props.trackFirstItem}
      >
        {({ registerStreamItemNode, unregisterStreamItemNode }) => {
          return (
            <PaginatedStream
              fetchNextPage={() => streamFetchMore(fetchMore, pageInfo)}
              isLoading={networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE}
              pageInfo={pageInfo}
              edges={edgesToRender}
              defaultCardHeight={props.defaultCardHeight}
              contentComponent={props.contentComponent}
              stream={stream}
              registerStreamItemNode={registerStreamItemNode}
              unregisterStreamItemNode={unregisterStreamItemNode}
              isClient={props.isClient}
              showSaveTag={props.showSaveTag}
            />
          );
        }}
      </AnalyticsWrapper>
    </StreamErrorWrapper>
  );
}

StreamWithData.defaultProps = {
  serverRenderedNodeCount: 3,
};

StreamWithData.propTypes = {
  networkStatus: PropTypes.number,
  error: PropTypes.instanceOf(Error),
  data: PropTypes.object,
  type: PropTypes.oneOf(Object.values(STREAM_TYPE)).isRequired,
  lookup: PropTypes.string.isRequired,
  onlyShowTitleForA11y: PropTypes.bool,
  defaultCardHeight: PropTypes.number.isRequired,
  contentComponent: PropTypes.func.isRequired,
  disableAnalytics: PropTypes.bool,
  disableUrlUpdating: PropTypes.bool,
  isClient: PropTypes.bool,
  serverRenderedNodeCount: PropTypes.number.isRequired,
  showSaveTag: PropTypes.bool,
  noResultsComponent: PropTypes.node,
  trackFirstItem: PropTypes.bool,
};
